import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { trackChatOpen, trackCalendlyClick } from '@/lib/analytics';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const ChatbotDemo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: "Hi! I'm the EZ Cloud AI assistant. Ask me about automating AP on Oracle — Fusion, EBS, PeopleSoft, or JD Edwards — our AI supplier-email exception prevention, ERP integration, or ROI. How can I help?" }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  // Per-conversation session ID — generated once, sent with every message so the
  // backend can group logged turns into a conversation.
  const sessionIdRef = useRef<string>('');
  if (!sessionIdRef.current && typeof crypto !== 'undefined' && crypto.randomUUID) {
    sessionIdRef.current = crypto.randomUUID();
  }

  // Keep the conversation pinned to the latest message, including while streaming.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: input.trim() };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setIsLoading(true);

    // Send the conversation (minus the initial greeting, which is assistant-first)
    // so the assistant has context. The API requires the first turn to be the user.
    const apiMessages = nextMessages.slice(1).map((m) => ({ role: m.role, content: m.content }));

    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: apiMessages,
          stream: true,
          session_id: sessionIdRef.current || undefined,
        }),
      });

      if (!res.ok || !res.body) throw new Error(`Chat request failed (${res.status})`);

      // Stream tokens into a single assistant bubble as they arrive (added on the
      // first token so the "Thinking…" indicator shows until the response begins).
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      let started = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        if (!started) {
          started = true;
          setMessages((prev) => [...prev, { role: 'assistant', content: acc }]);
        } else {
          setMessages((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { role: 'assistant', content: acc };
            return copy;
          });
        }
      }
      if (!started || !acc.trim()) throw new Error('Empty response');
    } catch (error) {
      console.error('Chat error:', error);
      toast({
        title: 'Error',
        description: 'Sorry — I had trouble responding. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
  };

  return (
    <>
      {/* Chat Button */}
      <Button
        onClick={() => {
          if (!isOpen) trackChatOpen('chatbot_demo');
          setIsOpen(!isOpen);
        }}
        className="fixed bottom-6 right-6 h-16 w-16 rounded-full shadow-2xl hover:shadow-[0_0_30px_rgba(0,0,0,0.3)] bg-primary hover:bg-primary/90 hover:scale-110 transition-all duration-200 z-50 border-4 border-white ring-2 ring-primary/20"
        aria-label={isOpen ? "Close chat assistant" : "Open chat assistant"}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <X className="h-7 w-7 text-white" />
        ) : (
          <MessageCircle className="h-7 w-7 text-white" />
        )}
      </Button>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-24 right-6 w-96 h-[500px] shadow-large z-50 flex flex-col">
          {/* Header */}
          <div className="bg-primary text-primary-foreground p-4 rounded-t-lg">
            <h3 className="font-semibold text-lg">EZ Cloud AI Assistant</h3>
            <p className="text-sm opacity-90">Ask me anything about AP automation</p>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message.content.split(/(\[.*?\]\(.*?\))/g).map((part, i) => {
                      const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
                      if (linkMatch) {
                        const url = linkMatch[2];
                        // Booking is on Microsoft Bookings (outlook.office.com/bookwithme);
                        // keep calendly.com for any legacy links.
                        const isBooking =
                          url.includes('bookwithme') || url.includes('calendly.com');
                        return (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              if (isBooking) trackCalendlyClick('chatbot');
                            }}
                            className="inline-flex items-center gap-1 text-primary hover:text-primary/80 underline font-semibold transition-colors"
                          >
                            {linkMatch[1]}
                          </a>
                        );
                      }
                      return part;
                    })}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Loading indicator — only until the first streamed token arrives */}
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex justify-start">
                <div className="bg-secondary text-secondary-foreground rounded-lg p-3 flex items-center space-x-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            )}
            
            {/* Demo suggestions */}
            {messages.length === 1 && !isLoading && (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-muted-foreground">Try asking:</p>
                <button
                  onClick={() => handleSuggestionClick("How does EZ Cloud automate AP on Oracle Fusion or EBS?")}
                  className="w-full text-left p-2 text-sm bg-secondary hover:bg-secondary/80 rounded-md transition-colors"
                  aria-label="Ask how EZ Cloud automates AP on Oracle"
                >
                  "How does EZ Cloud automate AP on Oracle Fusion or EBS?"
                </button>
                <button
                  onClick={() => handleSuggestionClick("What makes your AI email exception prevention different?")}
                  className="w-full text-left p-2 text-sm bg-secondary hover:bg-secondary/80 rounded-md transition-colors"
                  aria-label="Ask about AI email exception prevention"
                >
                  "What makes your AI email exception prevention different?"
                </button>
                <button
                  onClick={() => handleSuggestionClick("Do you support PeopleSoft or JD Edwards?")}
                  className="w-full text-left p-2 text-sm bg-secondary hover:bg-secondary/80 rounded-md transition-colors"
                  aria-label="Ask about PeopleSoft and JD Edwards support"
                >
                  "Do you support PeopleSoft or JD Edwards?"
                </button>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                placeholder="Type your question..."
                className="flex-1"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={isLoading}
              />
              <Button 
                size="icon" 
                className="bg-primary hover:bg-primary/90" 
                onClick={handleSend}
                disabled={isLoading || !input.trim()}
                aria-label="Send message"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Powered by EZ Cloud AI · Conversations may be reviewed to improve responses
            </p>
          </div>
        </Card>
      )}
    </>
  );
};

export default ChatbotDemo;
